import React from 'react';
import { GlobalStyles as MuiGlobalStyles } from '@mui/material';
import { brandColors, designTokens } from './modernTheme';

const ModernGlobalStyles = () => (
  <MuiGlobalStyles
    styles={{
      '*': {
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
        fontFamily: designTokens.typography.fontFamily,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      },
      html: {
        height: '100%',
        background: brandColors.neutral[50],
        scrollBehavior: 'smooth',
        fontSize: '16px',
        lineHeight: 1.6,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      body: {
        height: '100%',
        color: brandColors.neutral[900],
        backgroundColor: brandColors.neutral[50],
      },
      '#root': {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
      a: {
        textDecoration: 'none',
        color: brandColors.primary[500],
        transition: 'color 0.2s ease-in-out',
        '&:hover': {
          color: brandColors.primary[700],
        },
      },
      input: {
        fontFamily: designTokens.typography.fontFamily,
      },

      // Layout utilities modernos
      '.flex': {
        display: 'flex',
        '&.items-start': { alignItems: 'flex-start' },
        '&.items-center': { alignItems: 'center' },
        '&.items-end': { alignItems: 'flex-end' },
        '&.items-stretch': { alignItems: 'stretch' },
        '&.justify-start': { justifyContent: 'flex-start' },
        '&.justify-center': { justifyContent: 'center' },
        '&.justify-end': { justifyContent: 'flex-end' },
        '&.justify-between': { justifyContent: 'space-between' },
        '&.justify-around': { justifyContent: 'space-around' },
        '&.justify-evenly': { justifyContent: 'space-evenly' },
        '&.flex-col': { flexDirection: 'column' },
        '&.flex-row': { flexDirection: 'row' },
        '&.flex-wrap': { flexWrap: 'wrap' },
        '&.gap-1': { gap: designTokens.spacing.xs },
        '&.gap-2': { gap: designTokens.spacing.sm },
        '&.gap-3': { gap: designTokens.spacing.md },
        '&.gap-4': { gap: designTokens.spacing.lg },
        '&.gap-6': { gap: designTokens.spacing.xl },
        '&.gap-8': { gap: designTokens.spacing.xxl },
      },

      '.grid': {
        display: 'grid',
        '&.grid-cols-1': { gridTemplateColumns: 'repeat(1, 1fr)' },
        '&.grid-cols-2': { gridTemplateColumns: 'repeat(2, 1fr)' },
        '&.grid-cols-3': { gridTemplateColumns: 'repeat(3, 1fr)' },
        '&.grid-cols-4': { gridTemplateColumns: 'repeat(4, 1fr)' },
        '&.grid-cols-6': { gridTemplateColumns: 'repeat(6, 1fr)' },
        '&.grid-cols-12': { gridTemplateColumns: 'repeat(12, 1fr)' },
        '&.gap-1': { gap: designTokens.spacing.xs },
        '&.gap-2': { gap: designTokens.spacing.sm },
        '&.gap-3': { gap: designTokens.spacing.md },
        '&.gap-4': { gap: designTokens.spacing.lg },
        '&.gap-6': { gap: designTokens.spacing.xl },
        '&.gap-8': { gap: designTokens.spacing.xxl },
      },

      // Form grid moderno e responsivo
      '.form-grid': {
        display: 'grid',
        gap: designTokens.spacing.md,
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        alignContent: 'flex-start',
        '@media screen and (min-width: 768px)': {
          gridTemplateColumns: 'repeat(12, 1fr)',
          '.col-span-1': { gridColumn: 'span 1' },
          '.col-span-2': { gridColumn: 'span 2' },
          '.col-span-3': { gridColumn: 'span 3' },
          '.col-span-4': { gridColumn: 'span 4' },
          '.col-span-6': { gridColumn: 'span 6' },
          '.col-span-8': { gridColumn: 'span 8' },
          '.col-span-9': { gridColumn: 'span 9' },
          '.col-span-12': { gridColumn: 'span 12' },
        },
      },

      // Spacing utilities
      '.m-0': { margin: 0 },
      '.m-1': { margin: designTokens.spacing.xs },
      '.m-2': { margin: designTokens.spacing.sm },
      '.m-3': { margin: designTokens.spacing.md },
      '.m-4': { margin: designTokens.spacing.lg },
      '.m-6': { margin: designTokens.spacing.xl },
      '.m-8': { margin: designTokens.spacing.xxl },
      
      '.mt-0': { marginTop: 0 },
      '.mt-1': { marginTop: designTokens.spacing.xs },
      '.mt-2': { marginTop: designTokens.spacing.sm },
      '.mt-3': { marginTop: designTokens.spacing.md },
      '.mt-4': { marginTop: designTokens.spacing.lg },
      '.mt-6': { marginTop: designTokens.spacing.xl },
      '.mt-8': { marginTop: designTokens.spacing.xxl },
      
      '.mb-0': { marginBottom: 0 },
      '.mb-1': { marginBottom: designTokens.spacing.xs },
      '.mb-2': { marginBottom: designTokens.spacing.sm },
      '.mb-3': { marginBottom: designTokens.spacing.md },
      '.mb-4': { marginBottom: designTokens.spacing.lg },
      '.mb-6': { marginBottom: designTokens.spacing.xl },
      '.mb-8': { marginBottom: designTokens.spacing.xxl },
      
      '.ml-0': { marginLeft: 0 },
      '.ml-1': { marginLeft: designTokens.spacing.xs },
      '.ml-2': { marginLeft: designTokens.spacing.sm },
      '.ml-3': { marginLeft: designTokens.spacing.md },
      '.ml-4': { marginLeft: designTokens.spacing.lg },
      '.ml-6': { marginLeft: designTokens.spacing.xl },
      '.ml-8': { marginLeft: designTokens.spacing.xxl },
      
      '.mr-0': { marginRight: 0 },
      '.mr-1': { marginRight: designTokens.spacing.xs },
      '.mr-2': { marginRight: designTokens.spacing.sm },
      '.mr-3': { marginRight: designTokens.spacing.md },
      '.mr-4': { marginRight: designTokens.spacing.lg },
      '.mr-6': { marginRight: designTokens.spacing.xl },
      '.mr-8': { marginRight: designTokens.spacing.xxl },
      
      '.p-0': { padding: 0 },
      '.p-1': { padding: designTokens.spacing.xs },
      '.p-2': { padding: designTokens.spacing.sm },
      '.p-3': { padding: designTokens.spacing.md },
      '.p-4': { padding: designTokens.spacing.lg },
      '.p-6': { padding: designTokens.spacing.xl },
      '.p-8': { padding: designTokens.spacing.xxl },
      
      '.px-0': { paddingLeft: 0, paddingRight: 0 },
      '.px-1': { paddingLeft: designTokens.spacing.xs, paddingRight: designTokens.spacing.xs },
      '.px-2': { paddingLeft: designTokens.spacing.sm, paddingRight: designTokens.spacing.sm },
      '.px-3': { paddingLeft: designTokens.spacing.md, paddingRight: designTokens.spacing.md },
      '.px-4': { paddingLeft: designTokens.spacing.lg, paddingRight: designTokens.spacing.lg },
      '.px-6': { paddingLeft: designTokens.spacing.xl, paddingRight: designTokens.spacing.xl },
      '.px-8': { paddingLeft: designTokens.spacing.xxl, paddingRight: designTokens.spacing.xxl },
      
      '.py-0': { paddingTop: 0, paddingBottom: 0 },
      '.py-1': { paddingTop: designTokens.spacing.xs, paddingBottom: designTokens.spacing.xs },
      '.py-2': { paddingTop: designTokens.spacing.sm, paddingBottom: designTokens.spacing.sm },
      '.py-3': { paddingTop: designTokens.spacing.md, paddingBottom: designTokens.spacing.md },
      '.py-4': { paddingTop: designTokens.spacing.lg, paddingBottom: designTokens.spacing.lg },
      '.py-6': { paddingTop: designTokens.spacing.xl, paddingBottom: designTokens.spacing.xl },
      '.py-8': { paddingTop: designTokens.spacing.xxl, paddingBottom: designTokens.spacing.xxl },

      // Text utilities
      '.text-left': { textAlign: 'left' },
      '.text-center': { textAlign: 'center' },
      '.text-right': { textAlign: 'right' },
      '.text-justify': { textAlign: 'justify' },
      
      '.font-light': { fontWeight: designTokens.typography.fontWeights.light },
      '.font-normal': { fontWeight: designTokens.typography.fontWeights.regular },
      '.font-medium': { fontWeight: designTokens.typography.fontWeights.medium },
      '.font-semibold': { fontWeight: designTokens.typography.fontWeights.semibold },
      '.font-bold': { fontWeight: designTokens.typography.fontWeights.bold },
      
      '.text-xs': { fontSize: '0.75rem', lineHeight: '1rem' },
      '.text-sm': { fontSize: '0.875rem', lineHeight: '1.25rem' },
      '.text-base': { fontSize: '1rem', lineHeight: '1.5rem' },
      '.text-lg': { fontSize: '1.125rem', lineHeight: '1.75rem' },
      '.text-xl': { fontSize: '1.25rem', lineHeight: '1.75rem' },
      '.text-2xl': { fontSize: '1.5rem', lineHeight: '2rem' },
      '.text-3xl': { fontSize: '1.875rem', lineHeight: '2.25rem' },
      
      '.text-primary': { color: brandColors.primary[500] },
      '.text-secondary': { color: brandColors.secondary[500] },
      '.text-neutral-600': { color: brandColors.neutral[600] },
      '.text-neutral-700': { color: brandColors.neutral[700] },
      '.text-neutral-900': { color: brandColors.neutral[900] },
      '.text-success': { color: brandColors.success[500] },
      '.text-warning': { color: brandColors.warning[500] },
      '.text-error': { color: brandColors.error[500] },

      // Background utilities
      '.bg-white': { backgroundColor: '#ffffff' },
      '.bg-neutral-50': { backgroundColor: brandColors.neutral[50] },
      '.bg-neutral-100': { backgroundColor: brandColors.neutral[100] },
      '.bg-neutral-200': { backgroundColor: brandColors.neutral[200] },
      '.bg-primary': { backgroundColor: brandColors.primary[500] },
      '.bg-primary-50': { backgroundColor: brandColors.primary[50] },
      '.bg-secondary': { backgroundColor: brandColors.secondary[500] },
      '.bg-secondary-50': { backgroundColor: brandColors.secondary[50] },

      // Border utilities
      '.border': { border: `1px solid ${brandColors.neutral[200]}` },
      '.border-t': { borderTop: `1px solid ${brandColors.neutral[200]}` },
      '.border-b': { borderBottom: `1px solid ${brandColors.neutral[200]}` },
      '.border-l': { borderLeft: `1px solid ${brandColors.neutral[200]}` },
      '.border-r': { borderRight: `1px solid ${brandColors.neutral[200]}` },
      
      '.rounded': { borderRadius: designTokens.borderRadius.md },
      '.rounded-sm': { borderRadius: designTokens.borderRadius.sm },
      '.rounded-lg': { borderRadius: designTokens.borderRadius.lg },
      '.rounded-xl': { borderRadius: designTokens.borderRadius.xl },
      '.rounded-full': { borderRadius: designTokens.borderRadius.full },

      // Shadow utilities
      '.shadow-sm': { boxShadow: designTokens.shadows.sm },
      '.shadow': { boxShadow: designTokens.shadows.md },
      '.shadow-lg': { boxShadow: designTokens.shadows.lg },
      '.shadow-xl': { boxShadow: designTokens.shadows.xl },
      '.shadow-card': { boxShadow: designTokens.shadows.card },

      // Position utilities
      '.relative': { position: 'relative' },
      '.absolute': { position: 'absolute' },
      '.fixed': { position: 'fixed' },
      '.sticky': { position: 'sticky' },
      
      '.inset-0': { top: 0, right: 0, bottom: 0, left: 0 },
      '.top-0': { top: 0 },
      '.right-0': { right: 0 },
      '.bottom-0': { bottom: 0 },
      '.left-0': { left: 0 },

      // Display utilities
      '.block': { display: 'block' },
      '.inline-block': { display: 'inline-block' },
      '.inline': { display: 'inline' },
      '.hidden': { display: 'none' },
      
      // Overflow utilities
      '.overflow-hidden': { overflow: 'hidden' },
      '.overflow-auto': { overflow: 'auto' },
      '.overflow-x-auto': { overflowX: 'auto' },
      '.overflow-y-auto': { overflowY: 'auto' },

      // Width and height utilities
      '.w-full': { width: '100%' },
      '.w-auto': { width: 'auto' },
      '.h-full': { height: '100%' },
      '.h-auto': { height: 'auto' },
      '.min-h-screen': { minHeight: '100vh' },

      // Scrollbar moderno
      '.scrollbar-modern': {
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: brandColors.neutral[100],
          borderRadius: designTokens.borderRadius.full,
        },
        '&::-webkit-scrollbar-thumb': {
          background: brandColors.neutral[300],
          borderRadius: designTokens.borderRadius.full,
          '&:hover': {
            background: brandColors.neutral[400],
          },
        },
      },

      // Scrollbar global moderno
      '::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: brandColors.neutral[100],
        borderRadius: designTokens.borderRadius.full,
      },
      '::-webkit-scrollbar-thumb': {
        background: brandColors.neutral[300],
        borderRadius: designTokens.borderRadius.full,
        '&:hover': {
          background: brandColors.neutral[400],
        },
      },

      // Animações modernas
      '.animate-fade-in': {
        animation: 'fadeIn 0.3s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      },
      
      '.animate-slide-up': {
        animation: 'slideUp 0.3s ease-out',
        '@keyframes slideUp': {
          '0%': { opacity: 0, transform: 'translateY(20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      },
      
      '.animate-pulse-modern': {
        animation: 'pulseModern 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
        '@keyframes pulseModern': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.7 },
        },
      },
      
      '.animate-bounce-subtle': {
        animation: 'bounceSubtle 1s ease-in-out infinite',
        '@keyframes bounceSubtle': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-4px)' },
        },
      },

      // Hover effects modernos
      '.hover-lift': {
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: designTokens.shadows.lg,
        },
      },
      
      '.hover-scale': {
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'scale(1.02)',
        },
      },

      // Focus styles modernos
      '.focus-ring': {
        '&:focus': {
          outline: 'none',
          boxShadow: `0 0 0 3px ${brandColors.primary[500]}40`,
        },
      },

      // Responsive utilities expandidos
      '@media (max-width: 599px)': {
        '.xs\\:hidden': { display: 'none' },
        '.xs\\:block': { display: 'block' },
        '.xs\\:flex': { display: 'flex' },
        '.xs\\:grid': { display: 'grid' },
        '.xs\\:text-xs': { fontSize: '0.75rem' },
        '.xs\\:text-sm': { fontSize: '0.875rem' },
        '.xs\\:p-1': { padding: designTokens.spacing.xs },
        '.xs\\:p-2': { padding: designTokens.spacing.sm },
        '.xs\\:m-1': { margin: designTokens.spacing.xs },
        '.xs\\:m-2': { margin: designTokens.spacing.sm },
        '.xs\\:flex-col': { flexDirection: 'column' },
        '.xs\\:w-full': { width: '100%' },
        '.xs\\:text-center': { textAlign: 'center' },
      },
      
      '@media (max-width: 640px)': {
        '.sm\\:hidden': { display: 'none' },
        '.sm\\:block': { display: 'block' },
        '.sm\\:flex': { display: 'flex' },
        '.sm\\:grid': { display: 'grid' },
        '.sm\\:text-sm': { fontSize: '0.875rem' },
        '.sm\\:text-base': { fontSize: '1rem' },
        '.sm\\:p-2': { padding: designTokens.spacing.sm },
        '.sm\\:p-3': { padding: designTokens.spacing.md },
        '.sm\\:m-2': { margin: designTokens.spacing.sm },
        '.sm\\:m-3': { margin: designTokens.spacing.md },
        '.sm\\:flex-col': { flexDirection: 'column' },
        '.sm\\:gap-2': { gap: designTokens.spacing.sm },
        '.sm\\:rounded': { borderRadius: designTokens.borderRadius.md },
      },
      
      '@media (min-width: 600px) and (max-width: 959px)': {
        '.tablet\\:hidden': { display: 'none' },
        '.tablet\\:block': { display: 'block' },
        '.tablet\\:flex': { display: 'flex' },
        '.tablet\\:grid': { display: 'grid' },
        '.tablet\\:flex-row': { flexDirection: 'row' },
        '.tablet\\:gap-3': { gap: designTokens.spacing.md },
        '.tablet\\:p-4': { padding: designTokens.spacing.lg },
      },
      
      '@media (min-width: 768px)': {
        '.md\\:hidden': { display: 'none' },
        '.md\\:block': { display: 'block' },
        '.md\\:flex': { display: 'flex' },
        '.md\\:grid': { display: 'grid' },
        '.md\\:text-base': { fontSize: '1rem' },
        '.md\\:text-lg': { fontSize: '1.125rem' },
        '.md\\:p-4': { padding: designTokens.spacing.lg },
        '.md\\:p-6': { padding: designTokens.spacing.xl },
        '.md\\:m-4': { margin: designTokens.spacing.lg },
        '.md\\:flex-row': { flexDirection: 'row' },
        '.md\\:gap-4': { gap: designTokens.spacing.lg },
        '.md\\:rounded-lg': { borderRadius: designTokens.borderRadius.lg },
      },
      
      '@media (min-width: 1024px)': {
        '.lg\\:hidden': { display: 'none' },
        '.lg\\:block': { display: 'block' },
        '.lg\\:flex': { display: 'flex' },
        '.lg\\:grid': { display: 'grid' },
        '.lg\\:text-lg': { fontSize: '1.125rem' },
        '.lg\\:text-xl': { fontSize: '1.25rem' },
        '.lg\\:p-6': { padding: designTokens.spacing.xl },
        '.lg\\:p-8': { padding: designTokens.spacing.xxl },
        '.lg\\:m-6': { margin: designTokens.spacing.xl },
        '.lg\\:gap-6': { gap: designTokens.spacing.xl },
        '.lg\\:rounded-xl': { borderRadius: designTokens.borderRadius.xl },
      },
      
      '@media (min-width: 1280px)': {
        '.xl\\:hidden': { display: 'none' },
        '.xl\\:block': { display: 'block' },
        '.xl\\:flex': { display: 'flex' },
        '.xl\\:grid': { display: 'grid' },
        '.xl\\:text-xl': { fontSize: '1.25rem' },
        '.xl\\:text-2xl': { fontSize: '1.5rem' },
        '.xl\\:p-8': { padding: designTokens.spacing.xxl },
        '.xl\\:gap-8': { gap: designTokens.spacing.xxl },
      },
      
      // Utilities para orientação
      '@media (orientation: portrait)': {
        '.portrait\\:flex-col': { flexDirection: 'column' },
        '.portrait\\:w-full': { width: '100%' },
      },
      
      '@media (orientation: landscape)': {
        '.landscape\\:flex-row': { flexDirection: 'row' },
        '.landscape\\:h-full': { height: '100%' },
      },
      
      // Utilities para dispositivos touch
      '@media (hover: hover)': {
        '.hover\\:scale-105:hover': { transform: 'scale(1.05)' },
        '.hover\\:shadow-lg:hover': { boxShadow: designTokens.shadows.lg },
      },
      
      '@media (pointer: coarse)': {
        '.touch\\:p-4': { padding: designTokens.spacing.lg },
        '.touch\\:min-h-12': { minHeight: '48px' },
      },
      
      // Utilities para motion reduzido
      '@media (prefers-reduced-motion: reduce)': {
        '.motion-reduce\\:transition-none': { transition: 'none' },
        '.motion-reduce\\:animate-none': { animation: 'none' },
      },

      // Compatibilidade com classes antigas (para transição suave)
      '.flex.align-end': { alignItems: 'flex-end' },
      '.flex.center': { alignItems: 'center' },
      '.flex.justify-end': { justifyContent: 'flex-end' },
      '.flex.space-between': { justifyContent: 'space-between' },
      '.flex.justify-center': { justifyContent: 'center' },
      '.flex.column': { flexDirection: 'column' },
      '.mt_12': { marginTop: '12px' },
      '.mb_12': { marginBottom: '12px' },
      '.ml_12': { marginLeft: '12px' },
      '.mr_12': { marginRight: '12px' },
      '.scroll-x': { width: '100%', overflowX: 'auto' },
      '.absolute-center': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
    }}
  />
);

export default ModernGlobalStyles;